import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './MomentumRadar.css';
import WatchlistStar from './WatchlistStar';
import RefreshButton from './RefreshButton';

const MomentumSpike = ({ onBack }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('5min');
  const [trend, setTrend] = useState('high');

  const fetchData = useCallback(async (selectedTimeframe = timeframe) => {
    let isMounted = true;
    
    try {
      if (isMounted) setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/stockanalyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ timeframe: selectedTimeframe })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, ${errorText}`);
      }

      const data = await response.json();
      console.log('API Response:', data);

      if (isMounted) {
        if (data && Array.isArray(data.data) && data.data.length > 0) {
          console.log('First stock item:', data.data[0]);
          setStocks(data.data);
          setTimeframe(data.timeframe || '5min');
          setTrend(data.trend || 'high');
        } else {
          console.warn('No valid data received from API', data);
          setStocks([]);
        }
      }
    } catch (err) {
      console.error('Error in fetchData:', err);
      if (isMounted) {
        setError(err.message || 'Failed to fetch data');
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  }, [timeframe]);

  useEffect(() => {
    let isMounted = true;
    
    fetchData(timeframe);
    
    const intervalId = setInterval(() => {
      if (isMounted) {
        fetchData(timeframe);
      }
    }, 60000);
    
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [timeframe, fetchData]);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    fetchData(newTimeframe);
  };

  const formatNumber = useCallback((num) => {
    if (num === null || num === undefined) return 'N/A';
    const number = Number(num);
    return isNaN(number) ? 'N/A' : number.toFixed(2);
  }, []);

  const renderStockRow = useCallback((stock, index) => {
    const change = stock["Change (%)"] || 0;
    const lom = stock["LOM (%)"] || 0;
    const ltp = stock["LTP"] || 0;
    const stockName = stock["Stock"] || 'N/A';
    const lomTime = stock["LOM Time"];

    return (
      <tr key={`${stockName}-${index}`}>
        <td>
          <span className="stock-symbol">
            <WatchlistStar 
              stock={{
                symbol: stockName,
                name: stockName
              }}
            />
            {stockName}
          </span>
        </td>
        <td>{formatNumber(ltp)}</td>
        <td className={change >= 0 ? 'positive' : 'negative'}>
          {formatNumber(change)}%
        </td>
        <td className={lom >= 0 ? 'positive' : 'negative'}>
          {formatNumber(lom)}%
        </td>
        <td>
          {lomTime ? new Date(lomTime).toLocaleTimeString() : 'N/A'}
        </td>
      </tr>
    );
  }, [formatNumber]);

  return (
    <div className="momentum-spike-container">
      <header className="momentum-header">
        <div className="momentum-header-container">
          <button className="back-button" onClick={onBack}>← Back</button>
          <h1>Momentum Spike ({trend.toUpperCase()} - {timeframe})</h1>
          <RefreshButton 
            onRefresh={() => fetchData(timeframe)} 
            isLoading={loading}
            size="small"
          />
        </div>
        <div className="timeframe-buttons">
          <button 
            className={`timeframe-btn ${timeframe === '5min' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('5min')}
          >
            5 Min
          </button>
          <button 
            className={`timeframe-btn ${timeframe === '15min' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('15min')}
          >
            15 Min
          </button>
        </div>
      </header>

      <div className="table-responsive">
        <table className="stocks-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>LTP</th>
              <th>Change %</th>
              <th>LOM %</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5" className="text-center text-danger">
                  Error: {error}
                </td>
              </tr>
            ) : stocks && stocks.length > 0 ? (
              stocks.map(renderStockRow)
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(MomentumSpike);